<script>
    import { onMount } from 'svelte';
    import { formatEuroAmount } from './money.js';
    import { getApiBase } from './apiBase.js';
    import { getTeacherPricingSummary } from './teacherPricingSummary.js';

    export let embedded = false;
    export let teacherId = null;
    export let teacher = null; // optional: passed from dashboard for display name/email
    const apiBase = getApiBase();
    const PRICE_INPUT_STEP = 1;

    // State variables
    let teachers = [];
    let filteredTeachers = [];
    let selectedTeacherId = null;
    let isLoading = false;
    let isSaving = false;
    let errorMessage = '';
    let successMessage = '';
    let searchQuery = '';
    
    // Filter state
    let activeFilter = 'all'; // 'all', 'with_modifiers', 'without_modifiers'
    
    // Pricing modifier data
    let pricingModifiers = new Map(); // teacher_id -> modifier object
    let editingTeacherId = null;
    let editingModifier = null;

    // Manual class payment override data
    let selectedMonth = new Date();
    let teacherClasses = [];
    let isLoadingClasses = false;
    let currentClassesRequestKey = null;

    // Manual class override modal state
    let showOverrideModal = false;
    let selectedClassItem = null;
    let overridePrice = '';
    let overrideNotes = '';

    // Load teachers on component mount
    onMount(async () => {
        await loadTeachers();
        await loadPricingModifiers();
    });
    
    async function loadTeachers() {
        isLoading = true;
        errorMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/teachers-for-pricing/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to load teachers');
            }
            
            const data = await response.json();
            teachers = data.teachers || [];
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error loading teachers: ${error.message}`;
            console.error('Error loading teachers:', error);
        } finally {
            isLoading = false;
        }
    }
    
    async function loadPricingModifiers() {
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/teacher-pricing-modifiers/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                pricingModifiers = new Map();
                data.modifiers.forEach(modifier => {
                    pricingModifiers.set(modifier.teacher_id, modifier);
                });
                pricingModifiers = pricingModifiers; // Trigger reactivity
            }
            
        } catch (error) {
            console.error('Error loading teacher pricing modifiers:', error);
        }
    }
    
    function applyFilters() {
        let filtered = [...teachers];
        
        // Apply modifier filter
        if (activeFilter === 'with_modifiers') {
            filtered = filtered.filter(teacher => pricingModifiers.has(teacher.id));
        } else if (activeFilter === 'without_modifiers') {
            filtered = filtered.filter(teacher => !pricingModifiers.has(teacher.id));
        }
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(teacher => 
                teacher.first_name.toLowerCase().includes(query) ||
                teacher.last_name.toLowerCase().includes(query) ||
                teacher.email.toLowerCase().includes(query) ||
                teacher.username.toLowerCase().includes(query)
            );
        }
        
        filteredTeachers = filtered;
    }
    
    function setFilter(filter) {
        activeFilter = filter;
        applyFilters();
    }
    
    async function startEditingModifier(teacherId) {
        if (embedded && teacherId != null) {
            const exists = teachers.some(t => t.id === teacherId);
            if (!exists) {
                // Still allow editing by ID (modifier APIs are keyed by teacher_id),
                // but show a helpful warning for the UI/confirm messages.
                errorMessage = `Teacher metadata not found for ID ${teacherId}. You can still edit/reset pricing by ID.`;
            }
        }
        editingTeacherId = teacherId;
        const existingModifier = pricingModifiers.get(teacherId);
        
        // Default modifier structure with teacher pricing defaults
        editingModifier = {
            teacher_id: teacherId,
            individual_base_price: 15.00,
            pair_base_price: 18.00,
            group_base_price: 22.00,
            ib_surcharge: 5.00,
            special_needs_surcharge: 3.00,
            lithuanian_surcharge: 3.00,
            performance_bonus: 2.00,
            notes: '',
            is_active: true,
            ...existingModifier
        };

        await loadTeacherClassesForMonth(teacherId, selectedMonth);
    }

    async function loadTeacherClassesForMonth(teacherId, month) {
        isLoadingClasses = true;
        const token = localStorage.getItem('jwtToken');
        const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
        const requestKey = `${teacherId}:${monthStr}`;
        currentClassesRequestKey = requestKey;

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/teacher-classes-for-override/?teacher_id=${teacherId}&month=${monthStr}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (currentClassesRequestKey !== requestKey) {
                return;
            }

            if (response.ok) {
                const data = await response.json();
                teacherClasses = data.classes || [];
            } else {
                const errorData = await response.json();
                console.error('Error loading teacher classes:', errorData.message);
                teacherClasses = [];
            }
        } catch (error) {
            if (currentClassesRequestKey === requestKey) {
                console.error('Error loading teacher classes:', error);
                teacherClasses = [];
            }
        } finally {
            if (currentClassesRequestKey === requestKey) {
                isLoadingClasses = false;
            }
        }
    }

    function isClassOverridden(classItem) {
        return !!classItem?.is_overridden;
    }

    function openOverrideModal(classItem) {
        selectedClassItem = classItem;
        const current = isClassOverridden(classItem)
            ? (classItem.override_amount ?? classItem.price)
            : (classItem.original_price ?? classItem.price);
        overridePrice = current !== null && current !== undefined ? String(current) : '';
        overrideNotes = classItem.override_notes || '';
        showOverrideModal = true;
    }

    function closeOverrideModal() {
        showOverrideModal = false;
        selectedClassItem = null;
        overridePrice = '';
        overrideNotes = '';
    }

    async function saveClassOverride() {
        if (!selectedClassItem || !editingTeacherId) return;
        const token = localStorage.getItem('jwtToken');
        const parsed = parseFloat(overridePrice);
        if (Number.isNaN(parsed) || parsed < 0) {
            errorMessage = 'Override payment must be a non-negative number.';
            return;
        }

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/manual-teacher-class-payment/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    teacher_id: editingTeacherId,
                    class_id: selectedClassItem.id,
                    override_amount: parsed,
                    notes: overrideNotes || 'Manual teacher payment override set via admin interface'
                })
            });

            const data = await response.json();
            if (response.ok) {
                await loadTeacherClassesForMonth(editingTeacherId, selectedMonth);
                successMessage = data.message;
                setTimeout(() => { successMessage = ''; }, 3000);
                closeOverrideModal();
            } else {
                errorMessage = `Error saving override: ${data.message}`;
            }
        } catch (error) {
            errorMessage = `Error saving override: ${error.message}`;
        }
    }

    async function clearClassOverride() {
        if (!selectedClassItem || !editingTeacherId) return;
        const token = localStorage.getItem('jwtToken');

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/manual-teacher-class-payment/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    teacher_id: editingTeacherId,
                    class_id: selectedClassItem.id,
                    clear_override: true,
                    notes: overrideNotes || 'Manual teacher payment override cleared via admin interface'
                })
            });

            const data = await response.json();
            if (response.ok) {
                await loadTeacherClassesForMonth(editingTeacherId, selectedMonth);
                successMessage = data.message;
                setTimeout(() => { successMessage = ''; }, 3000);
                closeOverrideModal();
            } else {
                errorMessage = `Error clearing override: ${data.message}`;
            }
        } catch (error) {
            errorMessage = `Error clearing override: ${error.message}`;
        }
    }

    function setOverrideToZero() {
        overridePrice = '0';
    }

    function previousMonth() {
        selectedMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1);
        if (editingTeacherId) {
            loadTeacherClassesForMonth(editingTeacherId, selectedMonth);
        }
    }

    function nextMonth() {
        selectedMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1);
        if (editingTeacherId) {
            loadTeacherClassesForMonth(editingTeacherId, selectedMonth);
        }
    }

    function formatMonth(date) {
        return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' });
    }

    function formatDate(dateString) {
        if (!dateString) return 'No date';
        const date = new Date(dateString);
        return date.toLocaleDateString('lt-LT', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    
    function cancelEditingModifier() {
        editingTeacherId = null;
        editingModifier = null;
        teacherClasses = [];
        closeOverrideModal();
    }
    
    async function saveModifier() {
        if (!editingModifier) return;
        
        isSaving = true;
        errorMessage = '';
        successMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/teacher-pricing-modifiers/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editingModifier)
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to save teacher pricing modifier');
            }
            
            // Update local state
            pricingModifiers.set(editingModifier.teacher_id, data.modifier);
            pricingModifiers = pricingModifiers; // Trigger reactivity
            
            const name = getTeacherName(editingModifier.teacher_id);
            successMessage = `Teacher pricing modifier saved successfully${name ? ` for ${name}` : ''}.`;
            cancelEditingModifier();
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error saving modifier: ${error.message}`;
            console.error('Error saving modifier:', error);
        } finally {
            isSaving = false;
        }
    }
    
    async function deleteModifier(teacherId) {
        const name = getTeacherName(teacherId);
        const label = name ? name : `Teacher ID ${teacherId}`;
        if (!confirm(`Are you sure you want to delete the pricing modifier for ${label}? This will restore default pricing behavior.`)) {
            return;
        }
        
        isSaving = true;
        errorMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/teacher-pricing-modifiers/${teacherId}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to delete teacher pricing modifier');
            }
            
            // Remove from local state
            pricingModifiers.delete(teacherId);
            pricingModifiers = pricingModifiers; // Trigger reactivity
            
            const deletedName = getTeacherName(teacherId);
            successMessage = `Teacher pricing modifier deleted${deletedName ? ` for ${deletedName}` : ''}. Default pricing will now apply.`;
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error deleting modifier: ${error.message}`;
            console.error('Error deleting modifier:', error);
        } finally {
            isSaving = false;
        }
    }
    
    function getTeacherName(teacherId) {
        const teacher = teachers.find(t => t.id === teacherId);
        if (teacher) return `${teacher.first_name} ${teacher.last_name}`;
        // In embedded mode, fall back to a stable label instead of “Unknown Teacher”
        if (embedded && teacherId != null) return `Teacher ID ${teacherId}`;
        return '';
    }
    
    // Reactive statements
    $: searchQuery, activeFilter, teachers, pricingModifiers, applyFilters();
</script>

<div class="pricing-modifier-container" class:embedded={embedded}>
    {#if embedded && teacherId}
        <h3>Teacher Pricing</h3>
        <p class="description">Custom pricing rules for the selected teacher.</p>

        {#if isLoading}
            <div class="loading">Loading teacher pricing...</div>
        {:else}
            {@const embeddedId = Number(teacherId)}
            {@const pricingTeacher = teachers.find(t => t.id === embeddedId)}
            {@const teacherModifier = pricingModifiers.get(embeddedId)}

            <div class="teachers-list single">
                <div class="teacher-item {teacherModifier ? 'has-modifier' : 'default-pricing'}">
                    <div class="teacher-info">
                        <div class="teacher-name">
                            {#if pricingTeacher}
                                {pricingTeacher.first_name} {pricingTeacher.last_name}
                                <span class="user-type teacher">teacher</span>
                            {:else if teacher?.name}
                                {teacher.name}
                                <span class="user-type teacher">teacher</span>
                            {:else}
                                Teacher ID {embeddedId}
                                <span class="modifier-status default">Metadata missing</span>
                            {/if}
                            {#if teacherModifier}
                                <span class="modifier-status custom">Custom Rates</span>
                            {:else}
                                <span class="modifier-status default">Default Rates</span>
                            {/if}
                        </div>
                        {#if pricingTeacher?.email}
                            <div class="teacher-email">{pricingTeacher.email}</div>
                        {:else if teacher?.email}
                            <div class="teacher-email">{teacher.email}</div>
                        {/if}
                        <div class="modifier-summary">
                            {getTeacherPricingSummary(teacherModifier)}
                        </div>
                    </div>
                    <div class="teacher-actions">
                        <button 
                            class="action-btn edit"
                            on:click={() => startEditingModifier(embeddedId)}
                            disabled={editingTeacherId !== null && editingTeacherId !== embeddedId}
                        >
                            {teacherModifier ? 'Edit Rates' : 'Set Custom Rates'}
                        </button>
                        {#if teacherModifier}
                            <button 
                                class="action-btn delete"
                                on:click={() => deleteModifier(embeddedId)}
                                disabled={isSaving}
                            >
                                Reset to Default
                            </button>
                        {/if}
                    </div>
                </div>
            </div>
        {/if}
    {:else}
        <h3>Teacher Pricing Modifier Management</h3>
        <p class="description">Manage custom pricing rules for individual teachers. Default rates: Individual €15, Pair €18, Group €22, plus standard surcharges (IB €5, Special Needs €3, Lithuanian €3) and €2 performance bonus.</p>
        
        <!-- Filter buttons -->
        <div class="filter-buttons">
            <button 
                class="filter-btn {activeFilter === 'all' ? 'active' : ''}"
                on:click={() => setFilter('all')}
            >
                All Teachers ({teachers.length})
            </button>
            <button 
                class="filter-btn {activeFilter === 'with_modifiers' ? 'active' : ''}"
                on:click={() => setFilter('with_modifiers')}
            >
                With Custom Rates ({pricingModifiers.size})
            </button>
            <button 
                class="filter-btn {activeFilter === 'without_modifiers' ? 'active' : ''}"
                on:click={() => setFilter('without_modifiers')}
            >
                Default Rates ({teachers.filter(t => !pricingModifiers.has(t.id)).length})
            </button>
        </div>
        
        <!-- Search bar -->
        <div class="search-container">
            <input 
                type="text" 
                placeholder="Search by name, email, or username..." 
                bind:value={searchQuery}
                class="search-input"
            />
        </div>
        
        <!-- Teachers list -->
        {#if isLoading}
            <div class="loading">Loading teachers...</div>
        {:else if filteredTeachers.length === 0}
            <div class="no-teachers">No teachers found</div>
        {:else}
            <div class="teachers-list">
                {#each filteredTeachers as teacher (teacher.id)}
                    {@const teacherModifier = pricingModifiers.get(teacher.id)}
                    <div class="teacher-item {teacherModifier ? 'has-modifier' : 'default-pricing'}">
                        <div class="teacher-info">
                            <div class="teacher-name">
                                {teacher.first_name} {teacher.last_name}
                                <span class="user-type teacher">teacher</span>
                                {#if teacherModifier}
                                    <span class="modifier-status custom">Custom Rates</span>
                                {:else}
                                    <span class="modifier-status default">Default Rates</span>
                                {/if}
                            </div>
                            <div class="teacher-email">{teacher.email}</div>
                            <div class="modifier-summary">
                                {getTeacherPricingSummary(teacherModifier)}
                            </div>
                        </div>
                        <div class="teacher-actions">
                            <button 
                                class="action-btn edit"
                                on:click={() => startEditingModifier(teacher.id)}
                                disabled={editingTeacherId !== null}
                            >
                                {teacherModifier ? 'Edit Rates' : 'Set Custom Rates'}
                            </button>
                            {#if teacherModifier}
                                <button 
                                    class="action-btn delete"
                                    on:click={() => deleteModifier(teacher.id)}
                                    disabled={isSaving}
                                >
                                    Reset to Default
                                </button>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    {/if}
    
    <!-- Editing modal -->
    {#if editingTeacherId && editingModifier}
        <div class="modal-overlay" on:click={cancelEditingModifier} on:keydown={(e) => e.key === 'Escape' && cancelEditingModifier()}>
            <div class="modal-content" on:click|stopPropagation on:keydown|stopPropagation>
                <div class="modal-header">
                    {#if embedded && !teachers.find(t => t.id === editingTeacherId)}
                        <h4>Pricing Rates (Teacher ID {editingTeacherId})</h4>
                    {:else}
                        <h4>Pricing Rates for {getTeacherName(editingTeacherId)}</h4>
                    {/if}
                    <button class="close-btn" on:click={cancelEditingModifier}>×</button>
                </div>
                
                <div class="modal-body">
                    <div class="section">
                        <h5>Base Prices by Student Count</h5>
                        <p class="section-description">Set custom base prices for classes based on number of students:</p>
                        
                        <div class="pricing-grid">
                            <div class="form-group">
                                <label for="individual-price">Individual Classes (1 student):</label>
                                <div class="price-input">
                                    <span class="currency">€</span>
                                    <input 
                                        id="individual-price"
                                        type="number" 
                                        step={PRICE_INPUT_STEP}
                                        min="0"
                                        bind:value={editingModifier.individual_base_price}
                                        class="form-input"
                                    />
                                    <span class="default-note">Default: €15</span>
                                </div>
                            </div>
                            
                            <div class="form-group">
                                <label for="pair-price">Pair Classes (2 students):</label>
                                <div class="price-input">
                                    <span class="currency">€</span>
                                    <input 
                                        id="pair-price"
                                        type="number" 
                                        step={PRICE_INPUT_STEP}
                                        min="0"
                                        bind:value={editingModifier.pair_base_price}
                                        class="form-input"
                                    />
                                    <span class="default-note">Default: €18</span>
                                </div>
                            </div>
                            
                            <div class="form-group">
                                <label for="group-price">Group Classes (3+ students):</label>
                                <div class="price-input">
                                    <span class="currency">€</span>
                                    <input 
                                        id="group-price"
                                        type="number" 
                                        step={PRICE_INPUT_STEP}
                                        min="0"
                                        bind:value={editingModifier.group_base_price}
                                        class="form-input"
                                    />
                                    <span class="default-note">Default: €22</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="section">
                        <h5>Special Conditions Surcharges</h5>
                        <p class="section-description">Set custom surcharges for special teaching conditions:</p>
                        
                        <div class="pricing-grid">
                            <div class="form-group">
                                <label for="ib-surcharge">IB Surcharge:</label>
                                <div class="price-input">
                                    <span class="currency">€</span>
                                    <input 
                                        id="ib-surcharge"
                                        type="number" 
                                        step={PRICE_INPUT_STEP}
                                        min="0"
                                        bind:value={editingModifier.ib_surcharge}
                                        class="form-input"
                                    />
                                    <span class="default-note">Default: €5</span>
                                </div>
                            </div>
                            
                            <div class="form-group">
                                <label for="special-needs-surcharge">Special Needs Surcharge:</label>
                                <div class="price-input">
                                    <span class="currency">€</span>
                                    <input 
                                        id="special-needs-surcharge"
                                        type="number" 
                                        step={PRICE_INPUT_STEP}
                                        min="0"
                                        bind:value={editingModifier.special_needs_surcharge}
                                        class="form-input"
                                    />
                                    <span class="default-note">Default: €3</span>
                                </div>
                            </div>
                            
                            <div class="form-group">
                                <label for="lithuanian-surcharge">Lithuanian Second Language Surcharge:</label>
                                <div class="price-input">
                                    <span class="currency">€</span>
                                    <input 
                                        id="lithuanian-surcharge"
                                        type="number" 
                                        step={PRICE_INPUT_STEP}
                                        min="0"
                                        bind:value={editingModifier.lithuanian_surcharge}
                                        class="form-input"
                                    />
                                    <span class="default-note">Default: €3</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="section">
                        <h5>Performance Bonus</h5>
                        <p class="section-description">Set custom bonus amount for teachers who meet all performance criteria:</p>
                        
                        <div class="form-group">
                            <label for="performance-bonus">Performance Bonus:</label>
                            <div class="price-input">
                                <span class="currency">€</span>
                                <input 
                                    id="performance-bonus"
                                    type="number" 
                                    step={PRICE_INPUT_STEP}
                                    min="0"
                                    bind:value={editingModifier.performance_bonus}
                                    class="form-input"
                                />
                                <span class="default-note">Default: €2</span>
                            </div>
                            <p class="bonus-criteria">
                                <strong>Bonus criteria:</strong> ≥90% punctuality and teacher comments submitted for at least 93% of classes
                            </p>
                        </div>
                    </div>

                    <div class="section">
                        <h5>Manual Teacher Class Payment Overrides</h5>
                        <p class="section-description">Set a final teacher payment for individual classes (replaces the pricing engine result). Use month navigation to browse different months:</p>

                        <div class="month-selector">
                            <button type="button" class="month-nav-btn" on:click={previousMonth}>&lt;</button>
                            <span class="month-display">{formatMonth(selectedMonth)}</span>
                            <button type="button" class="month-nav-btn" on:click={nextMonth}>&gt;</button>
                        </div>

                        {#if isLoadingClasses}
                            <p class="loading-classes">Loading classes...</p>
                        {:else if teacherClasses.length === 0}
                            <p class="no-classes">No classes found for this month.</p>
                        {:else}
                            <div class="classes-grid">
                                {#each teacherClasses as classItem (classItem.id)}
                                    {@const overridden = isClassOverridden(classItem)}
                                    {@const isUpcomingClass = new Date(classItem.date_local) > new Date()}
                                    <div
                                        class="class-discount-label"
                                        class:overridden={overridden}
                                        role="button"
                                        tabindex="0"
                                        on:click={() => openOverrideModal(classItem)}
                                        on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && openOverrideModal(classItem)}
                                    >
                                        <div class="class-info">
                                            <span class="class-name">{classItem.name}</span>
                                            <span class="class-date">{formatDate(classItem.date_local)}</span>
                                            <span class="class-date">Students: {(classItem.students || []).join(', ') || 'N/A'}</span>
                                            <span class="class-price" class:discounted-price={overridden}>
                                                €{formatEuroAmount(classItem.price)}
                                                {#if overridden}
                                                    <span class="original-price">(was €{formatEuroAmount(classItem.original_price)})</span>
                                                {/if}
                                            </span>
                                            {#if overridden}
                                                <span class="info-badge">Manual override applied</span>
                                            {:else if isUpcomingClass}
                                                <span class="info-badge">Upcoming class</span>
                                            {/if}
                                        </div>
                                    </div>
                                {/each}
                            </div>

                            <div class="discount-summary">
                                <p><strong>Summary:</strong> {teacherClasses.filter(c => isClassOverridden(c)).length} of {teacherClasses.length} classes overridden</p>
                                <p><strong>Total:</strong> €{teacherClasses.reduce((sum, c) => sum + (c.price || 0), 0).toFixed(2)}
                                    {#if teacherClasses.reduce((sum, c) => sum + (c.original_price || 0), 0) !== teacherClasses.reduce((sum, c) => sum + (c.price || 0), 0)}
                                        <span class="original-price">(was €{teacherClasses.reduce((sum, c) => sum + (c.original_price || 0), 0).toFixed(2)})</span>
                                    {/if}
                                </p>
                            </div>
                        {/if}
                    </div>

                    {#if showOverrideModal && selectedClassItem}
                        <div class="modal-overlay" on:click={closeOverrideModal} on:keydown={(e) => e.key === 'Escape' && closeOverrideModal()}>
                            <div class="modal-content" on:click|stopPropagation on:keydown|stopPropagation>
                                <div class="modal-header">
                                    <h4>Manual Teacher Payment Override</h4>
                                    <button class="close-btn" on:click={closeOverrideModal}>×</button>
                                </div>
                                <div class="modal-body">
                                    <p><strong>Class:</strong> {selectedClassItem.name}</p>
                                    <p><strong>Date:</strong> {formatDate(selectedClassItem.date_local)}</p>
                                    <p><strong>Students:</strong> {(selectedClassItem.students || []).join(', ') || 'N/A'}</p>

                                    <div class="form-group">
                                        <label for="override-price">New final teacher payment (€):</label>
                                        <input
                                            id="override-price"
                                            type="number"
                                            step={PRICE_INPUT_STEP}
                                            min="0"
                                            bind:value={overridePrice}
                                            class="form-input"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="override-notes">Notes (optional):</label>
                                        <textarea
                                            id="override-notes"
                                            rows="3"
                                            bind:value={overrideNotes}
                                            class="form-input"
                                            placeholder="Why was this override applied?"
                                        />
                                    </div>
                                </div>
                                <div class="modal-footer">
                                    <button type="button" class="action-btn delete" on:click={clearClassOverride}>
                                        Clear override
                                    </button>
                                    <button type="button" class="action-btn" on:click={setOverrideToZero}>
                                        Set to €0
                                    </button>
                                    <button type="button" class="action-btn edit" on:click={saveClassOverride}>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    {/if}
                    
                    <div class="section">
                        <h5>Notes</h5>
                        <textarea 
                            bind:value={editingModifier.notes}
                            placeholder="Internal notes about why these custom rates were applied..."
                            rows="3"
                            class="form-textarea"
                        ></textarea>
                    </div>
                    
                    <div class="section">
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.is_active}
                            />
                            <span><strong>Active</strong> (uncheck to temporarily disable this modifier)</span>
                        </label>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button 
                        class="btn secondary"
                        on:click={cancelEditingModifier}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button 
                        class="btn primary"
                        on:click={saveModifier}
                        disabled={isSaving}
                    >
                        {#if isSaving}
                            Saving...
                        {:else}
                            Save Pricing Rates
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    {/if}
    
    <!-- Messages -->
    {#if errorMessage}
        <div class="error-message">{errorMessage}</div>
    {/if}
    
    {#if successMessage}
        <div class="success-message">{successMessage}</div>
    {/if}
</div>

<style>
    .pricing-modifier-container {
        max-width: 900px;
        margin: 0 auto;
        padding: 20px;
    }

    .pricing-modifier-container.embedded {
        max-width: none;
        margin: 0;
        padding: 0;
    }
    
    .description {
        color: #666;
        margin-bottom: 20px;
        font-style: italic;
    }
    
    .filter-buttons {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }
    
    .filter-btn {
        padding: 8px 16px;
        border: 1px solid #ddd;
        background: white;
        cursor: pointer;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s ease;
    }
    
    .filter-btn.active {
        background: #fab45a;
        color: black;
        border-color: #fab45a;
    }
    
    .filter-btn:hover:not(.active) {
        background: #f5f5f5;
    }
    
    .search-container {
        margin-bottom: 20px;
    }
    
    .search-input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
    }
    
    .loading, .no-teachers {
        text-align: center;
        padding: 40px;
        color: #666;
        font-style: italic;
    }
    
    .teachers-list {
        background: white;
        border: 1px solid #ddd;
        border-radius: 8px;
        overflow: hidden;
    }
    
    .teacher-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        transition: background-color 0.2s ease;
    }
    
    .teacher-item:last-child {
        border-bottom: none;
    }
    
    .teacher-item:hover {
        background-color: #f9f9f9;
    }
    
    .teacher-item.has-modifier {
        background-color: #fff8e1;
    }
    
    .teacher-item.has-modifier:hover {
        background-color: #fff3c4;
    }
    
    .teacher-info {
        flex: 1;
    }
    
    .teacher-name {
        font-weight: 500;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
    
    .user-type {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
    }
    
    .user-type.teacher {
        background-color: #f3e5f5;
        color: #7b1fa2;
    }
    
    .modifier-status {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
    }
    
    .modifier-status.custom {
        background-color: #fff3cd;
        color: #856404;
    }
    
    .modifier-status.default {
        background-color: #d4edda;
        color: #155724;
    }
    
    .teacher-email {
        color: #666;
        font-size: 13px;
        margin-bottom: 4px;
    }
    
    .modifier-summary {
        color: #666;
        font-size: 12px;
        font-style: italic;
    }
    
    .teacher-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
    }
    
    .action-btn {
        padding: 6px 12px;
        border: 1px solid;
        background: white;
        cursor: pointer;
        border-radius: 4px;
        font-size: 12px;
        transition: all 0.2s ease;
    }
    
    .action-btn.edit {
        border-color: #007bff;
        color: #007bff;
    }
    
    .action-btn.edit:hover:not(:disabled) {
        background: #007bff;
        color: white;
    }
    
    .action-btn.delete {
        border-color: #dc3545;
        color: #dc3545;
    }
    
    .action-btn.delete:hover:not(:disabled) {
        background: #dc3545;
        color: white;
    }
    
    .action-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    /* Modal styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;
    }
    
    .modal-content {
        background: white;
        border-radius: 8px;
        max-width: 700px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    
    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        background: #f8f9fa;
        border-radius: 8px 8px 0 0;
    }
    
    .modal-header h4 {
        margin: 0;
        color: #333;
    }
    
    .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
    }
    
    .close-btn:hover {
        background: #e9ecef;
        color: #333;
    }
    
    .modal-body {
        padding: 20px;
    }
    
    .section {
        margin-bottom: 25px;
        padding-bottom: 20px;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .section:last-child {
        border-bottom: none;
        margin-bottom: 0;
    }
    
    .section h5 {
        margin: 0 0 8px 0;
        color: #333;
        font-size: 16px;
    }
    
    .section-description {
        margin: 0 0 15px 0;
        color: #666;
        font-size: 13px;
        font-style: italic;
    }
    
    .pricing-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 15px;
    }
    
    @media (min-width: 600px) {
        .pricing-grid {
            grid-template-columns: 1fr 1fr;
        }
    }
    
    .form-group {
        margin-bottom: 15px;
    }
    
    .form-group label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
        color: #333;
        font-size: 14px;
    }
    
    .price-input {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 5px;
    }
    
    .currency {
        font-weight: bold;
        color: #333;
        font-size: 16px;
    }
    
    .form-input, .form-textarea {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }
    
    .price-input .form-input {
        max-width: 120px;
    }
    
    .default-note {
        font-size: 12px;
        color: #666;
        font-style: italic;
    }
    
    .form-textarea {
        resize: vertical;
        min-height: 80px;
    }
    
    .bonus-criteria {
        margin-top: 10px;
        font-size: 12px;
        color: #666;
        font-style: italic;
        line-height: 1.4;
    }
    
    .checkbox-label {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        cursor: pointer;
        padding: 8px;
        border-radius: 4px;
        transition: background-color 0.2s ease;
    }
    
    .checkbox-label:hover {
        background-color: #f8f9fa;
    }
    
    .checkbox-label input[type="checkbox"] {
        margin: 2px 0 0 0;
        cursor: pointer;
    }
    
    .checkbox-label span {
        font-size: 14px;
        line-height: 1.4;
    }

    .month-selector {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 15px;
        margin-bottom: 20px;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 6px;
    }

    .month-nav-btn {
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 8px 12px;
        cursor: pointer;
        font-weight: bold;
        transition: background-color 0.2s ease;
    }

    .month-nav-btn:hover {
        background: #0056b3;
    }

    .month-display {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        min-width: 150px;
        text-align: center;
    }

    .loading-classes, .no-classes {
        text-align: center;
        padding: 20px;
        color: #666;
        font-style: italic;
        background: #f8f9fa;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
    }

    .classes-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 8px;
        margin-bottom: 20px;
        max-height: 300px;
        overflow-y: auto;
        padding: 10px;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        background: #fafafa;
    }

    .class-discount-label {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .class-discount-label:hover {
        background: #f0f8ff;
        border-color: #007bff;
    }

    .class-discount-label.overridden {
        background: #fff3cd;
        border-color: #fab45a;
    }

    .class-discount-label.overridden:hover {
        background: #ffe8b6;
    }

    .class-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .class-name {
        font-weight: 600;
        color: #333;
        font-size: 14px;
        line-height: 1.3;
    }

    .class-date {
        font-size: 12px;
        color: #666;
    }

    .class-price {
        font-size: 13px;
        font-weight: 600;
        color: #28a745;
    }

    .class-price.discounted-price {
        color: #007bff;
    }

    .original-price {
        font-size: 11px;
        color: #999;
        text-decoration: line-through;
        font-weight: normal;
    }

    .info-badge {
        font-size: 11px;
        color: #856404;
        font-weight: 600;
    }

    .discount-summary {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 6px;
        border: 1px solid #e9ecef;
        margin-top: 15px;
    }

    .discount-summary p {
        margin: 5px 0;
        font-size: 14px;
        line-height: 1.4;
        color: #495057;
    }
    
    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 20px;
        border-top: 1px solid #eee;
        background: #f8f9fa;
        border-radius: 0 0 8px 8px;
    }
    
    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .btn.primary {
        background: #28a745;
        color: white;
    }
    
    .btn.primary:hover:not(:disabled) {
        background: #218838;
    }
    
    .btn.secondary {
        background: #6c757d;
        color: white;
    }
    
    .btn.secondary:hover:not(:disabled) {
        background: #545b62;
    }
    
    .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    
    .error-message {
        background: #f8d7da;
        color: #721c24;
        padding: 12px;
        border-radius: 6px;
        margin-top: 20px;
        border: 1px solid #f5c6cb;
    }
    
    .success-message {
        background: #d4edda;
        color: #155724;
        padding: 12px;
        border-radius: 6px;
        margin-top: 20px;
        border: 1px solid #c3e6cb;
    }
    
    /* Responsive design */
    @media (max-width: 768px) {
        .pricing-modifier-container {
            padding: 15px;
        }
        
        .filter-buttons {
            flex-direction: column;
        }
        
        .teacher-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
        }
        
        .teacher-actions {
            align-self: stretch;
            justify-content: space-between;
        }
        
        .modal-overlay {
            padding: 10px;
        }
        
        .modal-content {
            max-height: 95vh;
        }
        
        .modal-footer {
            flex-direction: column;
        }
        
        .pricing-grid {
            grid-template-columns: 1fr;
        }
        
        .price-input {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
        }
        
        .price-input .form-input {
            max-width: none;
        }
    }
</style>
